import { useCallback, useEffect, useMemo, useState } from "react";
import { useSearchParams } from "react-router-dom";
import {
  Banner,
  CK_TASK_PRIORITIES,
  DataTable,
  StatGrid,
  StatTile,
  Tag,
  TaskDialog,
  ckConfirm,
  useCkToast,
} from "@NP-Webber/crm-kit/dist/next/index.js";
import type { CkDataColumn, CkMenuItem, CkTone } from "@NP-Webber/crm-kit/dist/next/index.js";
import { shellApi, type TaskDto } from "../../api/shellApi";
import { usersApi } from "../../api/usersApi";
import { toClientApiError } from "../../api/httpClient";
import { useAuth } from "../../auth/AuthProvider";
import { ListPageHeader } from "../../ui/ListPageHeader";
import { useQuickCreate } from "../../next/quickCreate";
import { EVENTS, notifyDataChanged, onEvent, openAutomations } from "../../next/events";
import { useScreenSettings } from "../../next/related/relatedStore";
import { TaskCard } from "./TaskCard";

const TASK_TYPES = [
  { value: "general", label: "כללי" },
  { value: "callback", label: "חזרה ללקוח" },
  { value: "meeting", label: "פגישה" },
  { value: "admin", label: "מנהלה" },
  { value: "other", label: "אחר" },
];

const STATUS_META: Record<string, { label: string; tone: CkTone }> = {
  pending: { label: "בהמתנה", tone: "warning" },
  in_progress: { label: "בטיפול", tone: "info" },
  completed: { label: "בוצע", tone: "success" },
  cancelled: { label: "בוטל", tone: "neutral" },
};
const PRIORITY_META = Object.fromEntries(CK_TASK_PRIORITIES.map((p) => [p.value, p])) as Record<string, { label: string; tone: CkTone }>;

type Filter = "open" | "mine" | "overdue" | "today" | "completed" | null;

function dueToIso(value: unknown): string | null {
  if (value == null || value === "") return null;
  const d = new Date(String(value).replace(" ", "T"));
  return Number.isNaN(d.getTime()) ? null : d.toISOString();
}

const isOpen = (t: TaskDto) => t.status !== "completed" && t.status !== "cancelled";
const isOverdue = (t: TaskDto) => isOpen(t) && Boolean(t.dueAt) && new Date(t.dueAt as string).getTime() < Date.now();
const isToday = (t: TaskDto) => Boolean(t.dueAt) && new Date(t.dueAt as string).toDateString() === new Date().toDateString();

type Row = TaskDto & { assigneeName: string; statusLabel: string; priorityLabel: string; overdue: boolean };

export function TasksListPage() {
  const auth = useAuth();
  const toast = useCkToast();
  const [searchParams, setSearchParams] = useSearchParams();
  const [items, setItems] = useState<TaskDto[]>([]);
  const [members, setMembers] = useState<Array<{ userId: string; displayName: string }>>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [filter, setFilter] = useState<Filter>("open");
  const [dialog, setDialog] = useState<{ task: TaskDto | null } | null>(null);
  const [cardOpen, setCardOpen] = useState(false);
  const [selected, setSelected] = useState<TaskDto | null>(null);

  const reload = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const next = await shellApi.listTasks();
      setItems(next);
      setSelected((current) => (current ? (next.find((item) => item.id === current.id) ?? null) : null));
    } catch (e) {
      setError(toClientApiError(e).message);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    void reload();
    void usersApi
      .list()
      .then(({ items: userItems }) =>
        setMembers(userItems.map((m) => ({ userId: m.userId, displayName: m.displayName || m.email }))),
      )
      .catch(() => setMembers([]));
  }, [reload]);
  useEffect(() => onEvent<{ entity?: string }>(EVENTS.dataChanged, (d) => {
    if (!d?.entity || d.entity === "task") void reload();
  }), [reload]);

  useQuickCreate("task", () => setDialog({ task: null }));
  useScreenSettings([
    {
      key: "tasks-automations",
      title: "משימות אוטומטיות",
      description: "משימה שנפתחת לבד — לקוח חדש, הגשת טופס, הרשאה שפגה…",
      icon: "sparkle",
      onClick: () => openAutomations({ tab: "available" }),
    },
  ]);

  const nameByUserId = useMemo(() => new Map(members.map((m) => [m.userId, m.displayName])), [members]);
  const userOptions = useMemo(() => members.map((m) => ({ value: m.userId, label: m.displayName })), [members]);

  // deep link: /tasks?task=<id> (global search, Today)
  const taskParam = searchParams.get("task");
  useEffect(() => {
    if (!taskParam || !items.length) return;
    const t = items.find((x) => x.id === taskParam);
    if (t) {
      setSelected(t);
      setCardOpen(true);
    }
  }, [taskParam, items]);

  const me = auth.user?.id ?? null;
  const counts = useMemo(
    () => ({
      open: items.filter(isOpen).length,
      mine: items.filter((t) => isOpen(t) && t.assignedToUserId === me).length,
      overdue: items.filter(isOverdue).length,
      today: items.filter((t) => isOpen(t) && isToday(t)).length,
      completed: items.filter((t) => t.status === "completed").length,
    }),
    [items, me],
  );

  const rows: Row[] = useMemo(
    () =>
      items
        .filter((t) => {
          if (filter === "open") return isOpen(t);
          if (filter === "mine") return isOpen(t) && t.assignedToUserId === me;
          if (filter === "overdue") return isOverdue(t);
          if (filter === "today") return isOpen(t) && isToday(t);
          if (filter === "completed") return t.status === "completed";
          return true;
        })
        .map((t) => ({
          ...t,
          assigneeName: t.assignedToUserId ? (nameByUserId.get(t.assignedToUserId) ?? "") : "",
          statusLabel: STATUS_META[t.status]?.label ?? t.status,
          priorityLabel: PRIORITY_META[t.priority]?.label ?? t.priority,
          overdue: isOverdue(t),
        })),
    [items, filter, me, nameByUserId],
  );

  const update = async (id: string, patch: Partial<TaskDto>, okMsg?: string) => {
    try {
      await shellApi.updateTask(id, patch);
      if (okMsg) toast.success(okMsg);
      notifyDataChanged("task");
      await reload();
    } catch (e) {
      toast.error(toClientApiError(e).message);
    }
  };

  const remove = async (t: TaskDto) => {
    if (!(await ckConfirm({ title: "מחיקת משימה", message: `למחוק את „${t.title}”?`, confirmLabel: "מחיקה" }))) return;
    try {
      await shellApi.deleteTask(t.id);
      if (selected?.id === t.id) {
        setCardOpen(false);
        setSelected(null);
      }
      toast.success("המשימה נמחקה");
      notifyDataChanged("task");
      await reload();
    } catch (e) {
      toast.error(toClientApiError(e).message);
    }
  };

  const submitDialog = async (values: Record<string, string>) => {
    const title = String(values.description ?? "").trim();
    const typeLabel = TASK_TYPES.find((t) => t.value === values.task_type)?.label;
    const body = {
      title,
      priority: values.priority || "normal",
      dueAt: dueToIso(values.due_date),
      assignedToUserId: values.assigned_to || null,
      taskType: values.task_type || null,
    };
    if (dialog?.task) {
      await shellApi.updateTask(dialog.task.id, {
        ...body,
        status: values.status === "now" ? "in_progress" : values.status || dialog.task.status,
      });
      toast.success("המשימה עודכנה");
    } else {
      const created = await shellApi.createTask({
        ...body,
        description: values.task_type === "other" && values.task_type_custom ? `סוג: ${values.task_type_custom}` : typeLabel ? `סוג: ${typeLabel}` : null,
        status: "pending",
      });
      toast.success("המשימה נוצרה");
      setSelected(created);
    }
    setDialog(null);
    notifyDataChanged("task");
    await reload();
  };

  const columns = useMemo<Array<CkDataColumn<Row>>>(
    () => [
      {
        key: "title",
        label: "משימה",
        type: "text",
        render: (r: Row) => (
          <span className={r.status === "completed" ? "tb-task--done" : undefined}>
            <b>{r.title}</b>
            {r.overdue ? (
              <>
                {" "}
                <Tag tone="danger">באיחור</Tag>
              </>
            ) : null}
          </span>
        ),
      },
      {
        key: "statusLabel",
        label: "סטטוס",
        type: "enum",
        render: (r: Row) => <Tag tone={STATUS_META[r.status]?.tone ?? "neutral"}>{r.statusLabel}</Tag>,
      },
      {
        key: "priorityLabel",
        label: "עדיפות",
        type: "enum",
        render: (r: Row) => <Tag tone={PRIORITY_META[r.priority]?.tone ?? "neutral"}>{r.priorityLabel}</Tag>,
      },
      { key: "dueAt", label: "תאריך יעד", type: "date" },
      { key: "assigneeName", label: "אחראי", type: "enum", getValue: (r: Row) => r.assigneeName || "לא משויכת" },
      { key: "createdAt", label: "נוצרה", type: "date" },
    ],
    [],
  );

  const rowMenu = (r: Row): CkMenuItem[] => [
    { key: "open", label: "פתיחת כרטיס", icon: "external", onClick: () => openCard(r) },
    { key: "edit", label: "עריכה", icon: "edit", onClick: () => setDialog({ task: r }) },
    r.status !== "completed"
      ? { key: "done", label: "סימון כבוצע", icon: "check", onClick: () => void update(r.id, { status: "completed" }, "המשימה סומנה כבוצעה") }
      : { key: "reopen", label: "החזרה לפתוחה", icon: "undo", onClick: () => void update(r.id, { status: "pending" }, "המשימה נפתחה מחדש") },
    { divider: true },
    { key: "delete", label: "מחיקה", icon: "trash", danger: true, onClick: () => void remove(r) },
  ];

  const openCard = (t: TaskDto) => {
    setSelected(t);
    setCardOpen(true);
  };

  const dialogTask = dialog?.task
    ? {
        id: dialog.task.id,
        description: dialog.task.title,
        due_date: dialog.task.dueAt,
        priority: dialog.task.priority,
        status: dialog.task.status,
        assigned_to: dialog.task.assignedToUserId ?? "",
        task_type: dialog.task.taskType && TASK_TYPES.some((t) => t.value === dialog.task?.taskType) ? dialog.task.taskType : "general",
      }
    : null;

  return (
    <section className="page-section page-section--list">
      <ListPageHeader
        screen
        sub="משימות אישיות ושל הצוות · לחיצה על שורה פותחת את המשימה"
        onRefresh={() => void reload()}
        primaryLabel="משימה חדשה"
        onPrimary={() => setDialog({ task: null })}
        more={[{ key: "auto", label: "משימות אוטומטיות", icon: "sparkle", onClick: () => openAutomations({ tab: "available" }) }]}
      />

      {error ? <Banner tone="danger">{error}</Banner> : null}

      <StatGrid min={160}>
        <StatTile label="פתוחות" value={counts.open} icon="task" tone="primary" loading={loading && !items.length} active={filter === "open"} onClick={() => setFilter(filter === "open" ? null : "open")} />
        <StatTile label="שלי" value={counts.mine} icon="user" tone="violet" loading={loading && !items.length} active={filter === "mine"} onClick={() => setFilter(filter === "mine" ? null : "mine")} />
        <StatTile label="להיום" value={counts.today} icon="sun" tone="info" loading={loading && !items.length} active={filter === "today"} onClick={() => setFilter(filter === "today" ? null : "today")} />
        <StatTile label="באיחור" value={counts.overdue} icon="alert" tone="danger" warn={counts.overdue > 0} loading={loading && !items.length} active={filter === "overdue"} onClick={() => setFilter(filter === "overdue" ? null : "overdue")} />
        <StatTile label="בוצעו" value={counts.completed} icon="check" tone="success" loading={loading && !items.length} active={filter === "completed"} onClick={() => setFilter(filter === "completed" ? null : "completed")} />
      </StatGrid>

      <DataTable
        columns={columns}
        rows={rows}
        rowKey="id"
        loading={loading}
        storageKey="tb.tasks"
        onRowClick={(r: Row) => openCard(r)}
        activeRowId={cardOpen ? selected?.id : undefined}
        rowMenu={rowMenu}
        rowActions={(r: Row) =>
          r.status !== "completed"
            ? [{ key: "done", icon: "check", label: "סימון כבוצע", onClick: () => void update(r.id, { status: "completed" }, "המשימה סומנה כבוצעה") }]
            : []
        }
        bulkActions={[
          {
            key: "complete",
            label: "סימון כבוצע",
            icon: "check",
            onClick: async (sel: { ids: string[]; all: boolean }, clear: () => void) => {
              const ids = sel.all ? rows.map((r) => r.id) : sel.ids;
              await Promise.all(ids.map((id) => shellApi.updateTask(id, { status: "completed" })));
              clear();
              toast.success(`${ids.length} משימות סומנו כבוצעו`);
              notifyDataChanged("task");
              await reload();
            },
          },
        ]}
        searchPlaceholder="חיפוש משימה…"
        exportFilename="משימות"
        emptyTitle={filter ? "אין משימות בסינון הזה" : "אין משימות"}
        emptyText={filter ? "נסו לנקות את הסינון בכרטיסים למעלה." : "צרו משימה ראשונה — „משימה חדשה” למעלה."}
      />

      <TaskCard
        open={cardOpen}
        task={selected}
        onClose={() => {
          setCardOpen(false);
          setSelected(null);
          if (taskParam) {
            const next = new URLSearchParams(searchParams);
            next.delete("task");
            setSearchParams(next, { replace: true });
          }
        }}
        onChanged={() => {
          notifyDataChanged("task");
          void reload();
        }}
      />

      <TaskDialog
        open={Boolean(dialog)}
        task={dialogTask}
        onClose={() => setDialog(null)}
        onSubmit={submitDialog}
        taskTypes={TASK_TYPES}
        users={userOptions}
        defaultAssignee={me ?? ""}
      />
    </section>
  );
}
